import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useCreateFolder } from '@widgets/Files/hooks/useCreateFolder';
import { useGetFiles } from '@widgets/Files/hooks/useGetFiles';
import { useGetFoldersAndFilesFromParsedData } from '@widgets/Files/hooks/useGetFoldersAndFilesFromParsedData';
import { useRenameFolder } from '@widgets/Files/hooks/useRenameFolder';
import { ContextFiles } from '@widgets/Files/model/context';
import { folderModalData } from '@widgets/Files/model/data/folderModalData';
import { TFolderModes } from '@widgets/Files/model/types/modalMode.type';

interface IUseFolderModalProps {
    folderModalType: TFolderModes;
    onHideModal: () => void;
    isShow: boolean;
}

export const useFolderModal = ({ folderModalType, onHideModal, isShow }: IUseFolderModalProps) => {
    const { currentFolderDropdownId, currentFolderId } = useContext(ContextFiles);
    const { parsedFilesData } = useGetFiles(currentFolderId);

    const { folders } = useGetFoldersAndFilesFromParsedData({ parsedFilesData });
    const folderInfo = useMemo(
        () => folders.find((folder) => folder.id === currentFolderDropdownId),
        [currentFolderDropdownId, folders]
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const submitButtonRef = useRef(null);
    const { t } = useTranslation();
    const [inputValue, setInputValue] = useState(t('Files.FolderModal.Create.Input.Placeholder'));
    const [inputError, setInputError] = useState({ isError: false, text: '' });
    const createFolderMutation = useCreateFolder();
    const renameFolderMutation = useRenameFolder();

    useEffect(() => {
        if (createFolderMutation.isSuccess || renameFolderMutation.isSuccess) {
            onHideModal();
        }
    }, [renameFolderMutation, createFolderMutation, onHideModal]);

    useEffect(() => {
        if (isShow && folderModalType === 'folderEdit' && currentFolderDropdownId && folderInfo) {
            setInputValue(folderInfo.name);
        }
    }, [isShow, folderModalType, currentFolderDropdownId, folderInfo]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onHideModal();
            }
        };
        if (isShow) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isShow, onHideModal]);

    useEffect(() => {
        if (isShow) {
            submitButtonRef?.current?.focus();
            inputRef?.current?.select();
        }
    }, [isShow]);

    const handleInput = (name: string, value: string) => {
        setInputValue(value);
        setInputError({ isError: false, text: '' });
    };
    // const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     setInputValue(event.target.value);
    //     setInputError({ isError: false, text: '' });
    // };
    const onCreateFolder = () => {
        if (inputValue.trim().length === 0) {
            setInputError({ isError: true, text: t('Files.FolderModal.InputError.text') });
            return;
        }
        if (inputValue.trim().length > 255) {
            setInputError({
                isError: true,
                text: t('Files.FolderModal.InputError.maxLength.text'),
            });
            return;
        }
        createFolderMutation.mutate({ inputValue, createIn: currentFolderId || 'default' });
    };
    const onRenameFolder = () => {
        if (inputValue.trim().length === 0) {
            setInputError({ isError: true, text: t('Files.FolderModal.InputError.text') });
            return;
        }
        if (inputValue.trim().length > 255) {
            setInputError({
                isError: true,
                text: t('Files.FolderModal.InputError.maxLength.text'),
            });
            return;
        }
        renameFolderMutation.mutate({ folderId: currentFolderDropdownId, newName: inputValue });
    };
    const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (folderModalType === 'folderCreate') {
            onCreateFolder();
        }
        if (folderModalType === 'folderEdit') {
            onRenameFolder();
        }
    };
    const headerText = folderModalData.headerText[folderModalType];

    return {
        t,
        headerText,
        folderInfo,
        currentFolderDropdownId,
        currentFolderId,
        handleInput,
        handleSubmitForm,
        inputRef,
        inputValue,
        inputError,
        submitButtonRef,
    };
};
